// ============================================================
// EmbeddedSystemsTutorials.jsx
//
// Course content page: HANDS-ON lesson — the practical companion to
// EmbeddedSystems.jsx (the concepts lesson). The two previously
// overlapped almost completely: same architecture cards, the same
// register-blink snippet, the same Arduino/STM32 badges. This
// version is differentiated by role: it owns the hardware-family
// buying guide, a real parts list, and a distinct step-by-step
// worked example (an interrupt-driven button lab) instead of
// repeating the concepts page's register-manipulation illustration.
//
// Fixes applied:
//   1) the "GPIO_INIT.c" example mixed a bare statement sitting
//      outside any function (`DDRB |= (1 << 5);` at file scope —
//      not legal C) with a `void loop()` that nothing ever calls
//      (no Arduino framework and no main() to link against). It
//      would not compile. Replaced it entirely with a complete,
//      compiling Arduino-framework example — see below.
//   2) replaced the duplicated "blink via registers" example with a
//      genuinely different worked example: an interrupt-driven
//      button + LED lab (attachInterrupt), which is also a more
//      realistic "hands-on lab" artifact than repeating the
//      concepts page's illustration.
//   3) added a real, buyable parts list for the lab and the
//      capstone project, matching the pattern used across the IoT
//      lessons (component names + price ranges, not placeholders).
//   4) the copy button fired `navigator.clipboard.writeText()`
//      without awaiting it or catching a failure. Wrapped in
//      async/await + try/catch.
//   5) removed three dead imports (FaMemory, MdOutlinePrecisionManufacturing,
//      MdOutlineMemory — none were ever rendered).
//   6) re-themed from this lesson's one-off cyan/indigo palette to
//      the app-wide dark-purple/goldenrod look, moved to
//      arrow-function components, and replaced the Arduino/STM32
//      brand-logo icons with semantic Material Icons.
//   7) wired the "Start First Lab" button to actually navigate to
//      the new FirstLab.jsx page (it previously did nothing). See
//      the note at the bottom of this file for the route to add.
// ============================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CodeIcon from "@mui/icons-material/Code";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import MemoryIcon from "@mui/icons-material/Memory";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BuildIcon from "@mui/icons-material/Build";
import TerminalIcon from "@mui/icons-material/Terminal";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const MODULES = [
    { number: "01", title: "MCU vs MPU", description: "Learn why Microcontrollers (integrated RAM/Flash) win in embedded tasks over high-power Microprocessors." },
    { number: "02", title: "Memory Mapping", description: "Understand how code and data are stored in Flash and SRAM through the memory address space." },
    { number: "03", title: "Peripherals", description: "Master GPIO, Timers, ADC, and PWM — the hardware units that bridge code to physics." },
];

const MCU_FAMILIES = [
    { name: "ATmega328P", icon: <DeveloperBoardIcon />, color: BRAND.gold, sub: "Arduino Uno/Nano · ~$4", price: "$3–5" },
    { name: "STM32 (ARM)", icon: <DeveloperBoardIcon />, color: BRAND.teal, sub: "Industrial standard", price: "$2–10" },
    { name: "ESP32", icon: <MemoryIcon />, color: BRAND.gold, sub: "Wi-Fi/BLE onboard", price: "$4–8" },
    { name: "MSP430", icon: <BatteryFullIcon />, color: BRAND.teal, sub: "Ultra-low power", price: "$2–6" },
];

const LAB_PARTS = [
    { item: "Arduino Uno or Nano (ATmega328P)", price: "$4 – $12" },
    { item: "Momentary push button", price: "$0.10 – $0.50" },
    { item: "LED (any color) + 220Ω resistor", price: "$0.05 – $0.20" },
    { item: "Breadboard + jumper wires", price: "$3 – $6" },
];

const CAPSTONE_PARTS = [
    { item: "MFRC522 RFID reader + tag/card", price: "$2 – $5" },
    { item: "SG90 micro servo (acts as the lock bolt)", price: "$2 – $4" },
    { item: "16x2 LCD with I2C backpack", price: "$3 – $6" },
];

// A complete, compiling example. The original mixed a bare
// statement at file scope with a void loop() that nothing ever
// called. This is also a genuinely different worked example from
// the concepts lesson's register-level blink — it introduces
// hardware interrupts, which the "Bare Metal to RTOS" section below
// is actually about.
const ISR_BUTTON_SKETCH = `#define LED_PIN 13
#define BUTTON_PIN 2 // Must support external interrupts (INT0 on Uno/Nano)

volatile bool ledState = LOW;

void toggleLED() {
  ledState = !ledState; // kept short: ISRs should do minimal work
}

void setup() {
  pinMode(LED_PIN, OUTPUT);
  pinMode(BUTTON_PIN, INPUT_PULLUP);
  attachInterrupt(digitalPinToInterrupt(BUTTON_PIN), toggleLED, FALLING);
}

void loop() {
  digitalWrite(LED_PIN, ledState);
}`;

// Sub-components (arrow-function style, defined before first use)

const ModuleCard = ({ number, title, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Stack spacing={1.5}>
            <Typography variant="h4" sx={{ color: "rgba(255,255,255,0.08)", fontWeight: 900, mb: -2 }}>{number}</Typography>
            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>{description}</Typography>
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

const PartsList = ({ title, parts }) => (
    <Paper elevation={0} sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <ShoppingCartIcon sx={{ color: BRAND.gold, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Stack>
        <Stack spacing={1}>
            {parts.map((p) => (
                <Box key={p.item} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{p.item}</Typography>
                    <Typography variant="body2" sx={{ color: BRAND.teal, fontWeight: 700, whiteSpace: "nowrap" }}>{p.price}</Typography>
                </Box>
            ))}
        </Stack>
    </Paper>
);

// ── Main Page ───────────────────────────────────────────────────

const EmbeddedSystemsTutorials = () => {
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">
                {/* HERO SECTION */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                        label="Embedded Engineering · Module 02b: Hands-On"
                        sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Embedded <span style={{ color: BRAND.teal }}>Tutorials</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Picking up from the Concepts lesson: choose real hardware, wire it up, and write firmware
                        that reacts to the physical world in real time using interrupts.
                    </Typography>
                </Box>

                {/* 1. CORE ARCHITECTURE RECAP */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <AccountTreeIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Quick Recap</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {MODULES.map((item) => (
                            <Grid item xs={12} md={4} key={item.number}>
                                <ModuleCard {...item} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 10 }} />

                {/* 2. HANDS-ON LAB: INTERRUPTS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <CodeIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Lab: Interrupt-Driven Button</Typography>
                    </Box>
                    <Grid container spacing={6}>
                        <Grid item xs={12} md={5}>
                            <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                                Go beyond polling <code>digitalRead()</code> in a loop. An <strong>Interrupt Service
                                Routine (ISR)</strong> reacts to a button press within microseconds, without the
                                CPU ever having to check the pin itself.
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box component="ol" sx={{ pl: 3, m: 0, color: BRAND.muted, "& li": { mb: 1 } }}>
                                    <li>Wire the button between pin 2 and GND (the internal pull-up resistor handles the rest).</li>
                                    <li>Wire the LED (through its resistor) to pin 13.</li>
                                    <li>Flash the sketch, then press the button — the LED toggles on each press.</li>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal label="isr_button.ino" code={ISR_BUTTON_SKETCH} />
                            <PartsList title="Parts for This Lab" parts={LAB_PARTS} />
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. HARDWARE FAMILIES — buying guide */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>Which Board Should You Buy?</Typography>
                    <Grid container spacing={2} justifyContent="center">
                        {MCU_FAMILIES.map((mcu) => (
                            <Grid item xs={6} md={3} key={mcu.name}>
                                <Paper sx={{ p: 3, textAlign: "center", bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                    <Avatar sx={{ bgcolor: `${mcu.color}26`, color: mcu.color, mx: "auto", mb: 1 }}>{mcu.icon}</Avatar>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700 }}>{mcu.name}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>{mcu.sub}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700 }}>{mcu.price}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. CAPSTONE PREVIEW */}
                <Box sx={{ p: 5, borderRadius: 6, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, mb: 10 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <BuildIcon sx={{ color: BRAND.gold }} /> Capstone: Smart RFID Lock
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: BRAND.muted, fontSize: 15, lineHeight: 1.8, mb: 3 }}>
                                Build a production-style access control system. This project integrates SPI
                                communication, PWM motor control, and state-machine logic — all concepts
                                introduced in this lesson.
                            </Typography>
                            <Stack spacing={1}>
                                {["RFID Auth (MFRC522)", "Servo Control (PWM)", "LCD Status Feedback", "Secure EEPROM Storage"].map((feat) => (
                                    <Box key={feat} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <DoneIcon sx={{ color: BRAND.teal, fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ color: BRAND.muted }}>{feat}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <PartsList title="Parts for the Capstone" parts={CAPSTONE_PARTS} />
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                    <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Interface with Reality?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Grab your breadboard and start your first hardware initialization.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Chip label="Start First Lab" onClick={() => navigate("/embedded-first-lab")} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3, cursor: "pointer" }} />
                        <Chip label="Download Datasheets" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default EmbeddedSystemsTutorials;
